import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useEnqueueRich } from '../context/ToastContext';
import { api } from '../api/client';

export default function AchievementToast() {
  const { user } = useAuth();
  const enqueueRich = useEnqueueRich();

  useEffect(() => {
    if (!user || user.is_admin) return;

    api.get('/achievements').then(data => {
      if (!data || !data.achievements) return;

      // ── Completion toasts ────────────────────────────────────────────────
      const completionKey = `ach_toasted_${user.id}`;
      let shown = new Set();
      try {
        JSON.parse(localStorage.getItem(completionKey) || '[]').forEach(id => shown.add(id));
      } catch {}

      const completed = data.achievements.filter(a => a.completed);
      const newCompletions = completed.filter(a => !shown.has(a.id));

      completed.forEach(a => shown.add(a.id));
      localStorage.setItem(completionKey, JSON.stringify([...shown]));

      newCompletions.forEach(a => enqueueRich({ type: 'achievement', ...a }));

      // ── Progress toasts ──────────────────────────────────────────────────
      const progressKey = `ach_progress_${user.id}`;
      let storedProgress = {};
      try {
        storedProgress = JSON.parse(localStorage.getItem(progressKey) || '{}');
      } catch {}

      const justCompletedIds = new Set(newCompletions.map(a => a.id));
      const incomplete = data.achievements.filter(a => !a.completed);

      // Find incomplete achievements whose progress increased since last check
      const progressed = incomplete.filter(a => {
        const prev = storedProgress[a.id] ?? 0;
        return a.progress > prev && !justCompletedIds.has(a.id);
      });

      if (progressed.length > 0) {
        // Show the one closest to completion (highest %)
        const closest = progressed.reduce((best, a) => {
          const pct = a.progress / a.effective_requirement;
          const bestPct = best.progress / best.effective_requirement;
          return pct > bestPct ? a : best;
        });

        const prev = storedProgress[closest.id] ?? 0;
        const req = closest.effective_requirement;
        enqueueRich({
          type: 'progress',
          ...closest,
          fromPct: Math.min(100, Math.round((prev / req) * 100)),
          toPct: Math.min(100, Math.round((closest.progress / req) * 100)),
        });
      }

      // Save current progress for all incomplete achievements
      const updated = { ...storedProgress };
      incomplete.forEach(a => { updated[a.id] = a.progress; });
      localStorage.setItem(progressKey, JSON.stringify(updated));
    }).catch(() => {});
  }, [user, enqueueRich]);

  return null;
}
